/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件詳細ページ内のタブナビ（ダッシュボード / 要件定義 / 基本設計 / 打ち合わせ）。OpsTabs と同じ下線ナビ型タブで再現する
 * @graph-connects none
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';

/** タブ定義。 */
interface ProjectDetailTabDef {
  /** 遷移先ルート。 */
  to: string;
  /** 表示ラベルの翻訳キー（module scope では hook を呼べないため t() で解決する）。 */
  labelKey: string;
  /** 完全一致でのみアクティブにするか（index ルート用）。 */
  exact?: boolean;
  /** 遷移先キーを pjKey ではなく specPjKey にするか（要件定義タブ用）。 */
  useSpecKey?: boolean;
}

/** 案件詳細ページのタブ一覧。 @graph-connects none */
const PROJECT_DETAIL_TABS: ProjectDetailTabDef[] = [
  { to: '/projects/$pjKey', labelKey: 'projectDetail.tabs.dashboard', exact: true },
  { to: '/projects/$pjKey/spec', labelKey: 'projectDetail.tabs.spec', useSpecKey: true },
  { to: '/projects/$pjKey/basic-design', labelKey: 'projectDetail.tabs.basicDesign' },
  { to: '/projects/$pjKey/meetings', labelKey: 'projectDetail.tabs.meetings' },
];

/**
 * 案件詳細ページ内タブナビを描画する。
 * @graph-connects none
 */
export function ProjectDetailTabs({
  pjKey,
  specPjKey = pjKey,
}: {
  pjKey: string;
  /**
   * 要件定義タブの遷移先キー。Gen1統合プロダクトページでは自分自身（pjKey）、
   * チャンク分割済みGen2ページでは親のGen1キーになる（要件定義は分割前=Gen2時点でGen1キーの下に
   * 書かれているため）。未指定時は pjKey と同じ。
   */
  specPjKey?: string;
}): ReactNode {
  const { t } = useTranslation();
  return (
    <nav className="flex flex-wrap gap-1 border-b border-[var(--color-border)] pb-2">
      {PROJECT_DETAIL_TABS.map((tab) => (
        <Link
          key={tab.to}
          to={tab.to}
          params={{ pjKey: tab.useSpecKey === true ? specPjKey : pjKey }}
          activeOptions={{ exact: tab.exact === true }}
          className="rounded-lg px-3 py-1.5 text-sm font-medium text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-primary)]"
          activeProps={{
            className:
              'rounded-lg px-3 py-1.5 text-sm font-semibold bg-[var(--color-accent-primary)] text-white',
          }}
        >
          {t(tab.labelKey)}
        </Link>
      ))}
    </nav>
  );
}
